import React, { useState, useEffect, useCallback } from 'react';
import { get, post, API_URL } from '../../utils/api'; // Assuming api utils handle auth tokens
import { useAuth } from '../../context/AuthContext';
import Select from 'react-select';

// Default structure for new settings
const defaultSettingsStructure = {
  apiKeyLive: '',
  apiKeyTest: '',
  mode: 'TEST', 
  successRedirectUrl: '',
  cancelRedirectUrl: '',
  failureRedirectUrl: '',
  webhookEndpoint: '',
  isActive: false,
};

const BASE_WEBHOOK_URL = 'https://api.rss.solar/cxf/uns/payment/webhook-events/';

// --- WebhookDetailsModal Component ---
const WebhookDetailsModal = ({ isOpen, onClose, webhook }) => {
    if (!isOpen || !webhook) return null;

    // Function to format date strings (copied from WebhookDisplayList for use here)
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleString();
        } catch (e) {
            return 'Invalid Date';
        }
    };

    // Decide which fields to show based on presence (DB vs Yoco)
    const fieldsToShow = [
        { label: 'Name', value: webhook.name || 'N/A' },
        { label: 'URL', value: webhook.url || 'N/A' },
        { label: 'Mode', value: webhook.mode || 'N/A' },
    ];

    if (webhook.hasOwnProperty('isActive')) { // DB Webhook specific fields
        fieldsToShow.push({ label: 'Active', value: webhook.isActive ? 'Yes' : 'No' });
        fieldsToShow.push({ label: 'Yoco ID', value: webhook.webhookId || 'N/A' });
        fieldsToShow.push({ label: 'DB UUID', value: webhook.uuid || 'N/A' });
        fieldsToShow.push({ label: 'Secret', value: webhook.secret || 'N/A' });
        fieldsToShow.push({ label: 'Created', value: formatDate(webhook.createdAt) });
        fieldsToShow.push({ label: 'Updated', value: formatDate(webhook.updatedAt) });
    } else { // Yoco API specific fields
        fieldsToShow.push({ label: 'Status', value: webhook.status || 'N/A' });
        fieldsToShow.push({ label: 'ID', value: webhook.id || 'N/A' }); // Yoco's primary ID
        // Add other relevant fields from Yoco API if needed
    }

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
            <div className="relative mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
                <div className="mt-3 text-center">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Webhook Details</h3>
                    <div className="mt-2 px-7 py-3">
                        <dl className="space-y-2 text-left">
                            {fieldsToShow.map(field => (
                                <div key={field.label} className="grid grid-cols-3 gap-4">
                                    <dt className="text-sm font-medium text-gray-500 col-span-1">{field.label}:</dt>
                                    <dd className="text-sm text-gray-900 col-span-2 break-words select-all">{field.value}</dd>
                                </div>
                            ))}
                        </dl>
                    </div>
                    <div className="items-center px-4 py-3">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 bg-gray-500 text-white text-base font-medium rounded-md w-auto shadow-sm hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-300"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
// --- End WebhookDetailsModal ---

// Helper component to display webhook list
const WebhookDisplayList = ({ title, webhooks, isLoading, errorMsg, source = "DB", onViewDetails }) => {
    // Function to format date strings (no longer needed in the list itself)
    // const formatDate = (dateString) => { ... }; 

    if (isLoading) return <div className="text-sm text-gray-600">Loading {title}...</div>;
    if (errorMsg) return <div className="text-sm text-red-600">Error loading {title}: {errorMsg}</div>;
    if (webhooks === null && source === "Yoco") return <div className="text-sm text-yellow-600">Could not fetch {title} (e.g., missing API key or settings). Click button above to try.</div>;
    if (webhooks === null && source === "DB") return <div className="text-sm text-yellow-600">Could not load {title}.</div>;

    if (webhooks.length === 0) return <div className="text-sm text-gray-500">No {title} webhooks found {source === 'DB' ? 'in database' : 'registered with Yoco'}.</div>;

    const isDbSource = source === 'DB';
    let headers = isDbSource
        ? ['Name', 'URL', 'Mode', 'Active', 'Yoco ID'] // Removed Created, Updated
        : ['Name', 'URL', 'Mode', 'Status']; 

    headers.push('Actions');

    return (
        <div>
            <h4 className="font-semibold mb-2">{title} ({webhooks.length}):</h4>
            <div className="overflow-x-auto">
                {/* Removed min-w-full, kept table-fixed */}
                <table className="w-full divide-y divide-gray-200 text-sm table-fixed">
                    <thead className="bg-gray-50">
                        <tr>
                            {/* Keep column width definitions */}
                             <th scope="col" className="w-1/4 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                             <th scope="col" className="w-1/3 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">URL</th>
                             <th scope="col" className="w-1/12 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mode</th>
                             <th scope="col" className="w-1/12 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{isDbSource ? 'Active' : 'Status'}</th>
                             {isDbSource && <th scope="col" className="w-1/6 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Yoco ID</th>}
                             <th scope="col" className="w-auto px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {webhooks.map((hook) => (
                            <tr key={hook.id || hook.uuid || hook.webhookId || hook.name}>
                                {/* Ensure Name also truncates if needed */}
                                <td className="px-4 py-2 whitespace-nowrap overflow-hidden text-ellipsis font-medium text-gray-900" title={hook.name || 'Unnamed'}>{hook.name || 'Unnamed'}</td>
                                {/* Explicit overflow and ellipsis for URL */}
                                <td className="px-4 py-2 whitespace-nowrap overflow-hidden text-ellipsis text-gray-600" title={hook.url}>{hook.url}</td> 
                                <td className="px-4 py-2 whitespace-nowrap text-gray-600">{hook.mode || 'N/A'}</td>
                                <td className="px-4 py-2 whitespace-nowrap text-gray-600">
                                    {isDbSource
                                        ? (hook.isActive ? 'Yes' : 'No')
                                        : (hook.status === 'active' ? 'Yes' : (hook.status || 'Unknown'))}
                                </td>
                                {isDbSource && (
                                    <td className="px-4 py-2 whitespace-nowrap overflow-hidden text-ellipsis text-gray-600" title={hook.webhookId || 'N/A'}>{hook.webhookId || 'N/A'}</td>
                                )}
                                {/* Actions Cell */}
                                <td className="px-4 py-2 whitespace-nowrap text-right text-sm font-medium">
                                    <button 
                                        onClick={() => onViewDetails(hook)} 
                                        className="text-indigo-600 hover:text-indigo-900"
                                    >
                                        View Details
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

function YocoSettingsPage() {
  const { user } = useAuth(); // Get user info if needed
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [settings, setSettings] = useState(null);
  const [initialSettings, setInitialSettings] = useState(null); // To track changes
  const [loadingSites, setLoadingSites] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [siteError, setSiteError] = useState(null); // Separate error for site loading
  const [settingsError, setSettingsError] = useState(null); // Separate error for settings loading/saving
  const [successMessage, setSuccessMessage] = useState(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false); // Flag if we are creating new settings
  const [infoMessage, setInfoMessage] = useState(null); // State for informational messages
  
  // State for Yoco webhooks
  const [liveWebhooks, setLiveWebhooks] = useState(null);
  const [testWebhooks, setTestWebhooks] = useState(null);
  const [loadingYocoWebhooks, setLoadingYocoWebhooks] = useState(false);
  const [yocoWebhookError, setYocoWebhookError] = useState(null);

  // State for active tab
  const [activeTab, setActiveTab] = useState('settings'); // 'settings' or 'webhooks' or 'register'

  // State for webhook registration form
  const [webhookName, setWebhookName] = useState('');
  const [webhookMode, setWebhookMode] = useState('TEST'); // Default to Test mode
  const [registeringWebhook, setRegisteringWebhook] = useState(false);
  const [registrationError, setRegistrationError] = useState(null);
  const [registrationSuccess, setRegistrationSuccess] = useState(null);
  const [refreshWebhooksKey, setRefreshWebhooksKey] = useState(0); // To trigger webhook list refresh

  // State for Database webhooks
  const [dbWebhooks, setDbWebhooks] = useState(null);
  const [loadingDbWebhooks, setLoadingDbWebhooks] = useState(false);
  const [dbWebhookError, setDbWebhookError] = useState(null);
  const [refreshDbKey, setRefreshDbKey] = useState(0); // Key to trigger DB webhook refresh

  // State for details modal
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedWebhookDetails, setSelectedWebhookDetails] = useState(null);

  // Fetch available sites
  useEffect(() => {
    const fetchSites = async () => {
      setLoadingSites(true);
      setSiteError(null);
      try {
        // Corrected API endpoint for fetching sites
        const response = await get(`${API_URL}/site/sites`); 
        if (response.data && Array.isArray(response.data)) {
          // Sort sites alphabetically by name before setting state
          const sortedSites = response.data.sort((a, b) => a.name.localeCompare(b.name));
          setSites(sortedSites.map(site => ({ value: site.uuid, label: site.name })));
        } else {
          setSites([]);
          setSiteError('Failed to load sites or invalid format received.'); 
        }
      } catch (err) {
        // console.error('Error fetching sites:', err);
        setSiteError('Failed to load sites. Check network connection and permissions.');
        setSites([]);
      } finally {
        setLoadingSites(false);
      }
    };
    fetchSites();
  }, []);

  // Fetch settings or setup for creation when a site is selected
  const loadOrCreateSettings = useCallback(async (siteUuid) => {
    if (!siteUuid) return;
    setLoadingSettings(true);
    setSettingsError(null);
    setSuccessMessage(null);
    setInfoMessage(null); 
    setSettings(null);
    setInitialSettings(null);
    setIsCreatingNew(false);
    
    // Construct the default webhook URL for this site
    const defaultWebhookUrl = `${BASE_WEBHOOK_URL}${siteUuid}`;

    try {
      const response = await get(`${API_URL}/payment/settings`, { siteUuid });
      if (response.data && response.data.siteUuid) {
        const loadedSettings = response.data;
        // Check if existing webhookEndpoint is empty and pre-fill if necessary
        if (!loadedSettings.webhookEndpoint) {
           loadedSettings.webhookEndpoint = defaultWebhookUrl;
           // Don't set info message here, just pre-fill the value for convenience
           // console.log('Existing webhook endpoint was empty, pre-filling default.')
        }
        setSettings(loadedSettings);
        setInitialSettings(response.data); // Store the *original* data from API as initial
        setIsCreatingNew(false);
        // console.log('Loaded existing Yoco settings for site:', siteUuid);
      } else {
        // No valid data returned, initialize create form with default webhook
        setSettings({ 
            ...defaultSettingsStructure, 
            siteUuid: siteUuid, 
            webhookEndpoint: defaultWebhookUrl // Pre-fill webhook
        });
        setInitialSettings({ 
            ...defaultSettingsStructure, 
            siteUuid: siteUuid, 
            webhookEndpoint: defaultWebhookUrl // Also set in initial for create
        }); 
        setIsCreatingNew(true);
        setInfoMessage('No settings found for this site. Fill the form to create them.'); 
        // console.log('No valid settings data returned. Initializing create form:', siteUuid);
      }
    } catch (err) {
      if (err.response && err.response.status === 404) {
          // 404 means no active settings, initialize create form with default webhook
          setSettings({ 
              ...defaultSettingsStructure, 
              siteUuid: siteUuid, 
              webhookEndpoint: defaultWebhookUrl // Pre-fill webhook
          });
          setInitialSettings({ 
              ...defaultSettingsStructure, 
              siteUuid: siteUuid, 
              webhookEndpoint: defaultWebhookUrl // Also set in initial for create
          });
          setIsCreatingNew(true);
          setInfoMessage('No active settings found. Settings might exist but be inactive. Saving will create or overwrite.'); 
          // console.log('API returned 404. Initializing create/overwrite form for site:', siteUuid);
      } else {
          // console.error('Error fetching Yoco settings:', err);
          setSettingsError(`Failed to load Yoco settings. ${err.response?.data?.message || 'Cannot display form.'}`);
          setSettings(null);
          setInitialSettings(null);
          setIsCreatingNew(false);
      }
    } finally {
      setLoadingSettings(false);
    }
  }, []);

  useEffect(() => {
    if (selectedSite) {
      loadOrCreateSettings(selectedSite.value);
    } else {
      // Clear form if no site is selected
      setSettings(null);
      setInitialSettings(null);
      setSettingsError(null);
      setIsCreatingNew(false);
    }
  }, [selectedSite, loadOrCreateSettings]);

  // Fetch DB webhooks when site changes or refresh key increments
  useEffect(() => {
    const fetchDbWebhooks = async () => {
        const siteUuid = selectedSite?.value;
        if (!siteUuid) {
            setDbWebhooks(null); // Clear if no site selected
            return;
        }
        
        setLoadingDbWebhooks(true);
        setDbWebhookError(null);
        try {
            const response = await get(`${API_URL}/payment/webhooks`, { siteUuid }); // Use the new GET route
            setDbWebhooks(response.data || []); // Assuming array response
        } catch (err) {
            // console.error("Error fetching DB webhooks:", err);
            setDbWebhookError(`Failed to load webhooks from database: ${err.response?.data?.message || err.message}`);
            setDbWebhooks(null);
        } finally {
            setLoadingDbWebhooks(false);
        }
    };
    
    fetchDbWebhooks();
  }, [selectedSite, refreshDbKey]);

  // Handler to fetch Yoco webhooks ON DEMAND - Make sure state names are correct
  const handleFetchYocoWebhooks = async () => {
      const siteUuid = selectedSite?.value;
      if (!siteUuid || !settings) {
          setYocoWebhookError('Cannot fetch Yoco webhooks without a selected site and loaded settings.');
          return;
      }
      if (!settings.apiKeyLive && !settings.apiKeyTest) {
          setYocoWebhookError('No Live or Test API key saved in settings.');
          setLiveWebhooks([]);
          setTestWebhooks([]);
          return;
      }

      setLoadingYocoWebhooks(true); // Correct state setter
      setYocoWebhookError(null); // Correct state setter
      setLiveWebhooks(null); 
      setTestWebhooks(null);
      try {
          const response = await get(`${API_URL}/payment/yoco-webhooks`, { siteUuid });
          setLiveWebhooks(response.data.liveWebhooks);
          setTestWebhooks(response.data.testWebhooks);
          if (response.data.error) {
              setYocoWebhookError(response.data.error);
          }
      } catch (err) {
          // console.error('Error fetching Yoco webhooks via backend:', err);
          setYocoWebhookError(`Failed to fetch: ${err.response?.data?.message || err.message}`);
      } finally {
          setLoadingYocoWebhooks(false); // Correct state setter
      }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => {
      if (!prev) return null; // Should not happen if site is selected
      return {
         ...prev,
         [name]: type === 'checkbox' ? checked : value
      }
    });
  };

  const handleSelectChange = (name, selectedOption) => {
    setSettings(prev => {
       if (!prev) return null;
      return {
        ...prev,
        [name]: selectedOption.value
      }
    });
  };

  // Handle settings form submission
  const handleSettingsSubmit = async (e) => {
    e.preventDefault();
    if (!settings || !selectedSite) return;
    setSaving(true);
    setSettingsError(null);
    setSuccessMessage(null);
    setInfoMessage(null);
    try {
       const dataToSave = { ...settings };
       const response = await post(`${API_URL}/payment/settings`, dataToSave);
       if (response.data && response.data.siteUuid) {
            setSettings(response.data);
            setInitialSettings(response.data);
            setIsCreatingNew(false);
            setSuccessMessage(`Yoco settings ${isCreatingNew ? 'created' : 'updated'} successfully!`);
            // NOTE: No need to trigger DB webhook refresh here, only Yoco refresh if needed
       } else {
            throw new Error('Invalid data returned after saving settings.');
       }
    } catch (err) {
        // console.error('Error saving Yoco settings:', err);
        setSettingsError(`Failed to save Yoco settings. ${err.response?.data?.message || 'Please try again.'}`);
    } finally {
        setSaving(false);
        setTimeout(() => setSuccessMessage(null), 5000);
    }
  };
  
  // Handle webhook registration form submission
  const handleWebhookRegisterSubmit = async (e) => {
      e.preventDefault();
      if (!selectedSite || !webhookName || !settings?.webhookEndpoint) {
          setRegistrationError('Site must be selected, name entered, and Webhook Endpoint saved in settings first.');
          return;
      }
      
      setRegisteringWebhook(true);
      setRegistrationError(null);
      setRegistrationSuccess(null);
      
      const webhookData = {
          siteUuid: selectedSite.value,
          name: webhookName,
          url: settings.webhookEndpoint, // Use the URL saved in settings
          mode: webhookMode,
      };
      
      try {
          const result = await post(`${API_URL}/payment/webhooks`, webhookData);
          setRegistrationSuccess(`Webhook registered via DB successfully! (Details: ${JSON.stringify(result.data)})`);
          setWebhookName('');
          // Trigger DB webhook list refresh
          setRefreshDbKey(prev => prev + 1); 
      } catch (err) {
          // console.error('Error registering webhook:', err);
          setRegistrationError(`Failed to register webhook: ${err.response?.data?.message || err.message || 'Please try again.'}`);
      } finally {
          setRegisteringWebhook(false);
          setTimeout(() => setRegistrationSuccess(null), 8000);
      }
  };

  // Functions for modal - these should be correct
  const handleViewDetails = (webhook) => {
      // console.log("Opening modal for:", webhook); // Add console log for debugging
      setSelectedWebhookDetails(webhook);
      setDetailsModalOpen(true);
  };

  const handleCloseDetails = () => {
      setDetailsModalOpen(false);
      setSelectedWebhookDetails(null);
  };

  const hasChanged = settings && JSON.stringify(settings) !== JSON.stringify(initialSettings);

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6 text-gray-800">Yoco Payment Configuration</h1>

      {/* Site Loading Error */}
      {siteError && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4" role="alert">
          <strong className="font-bold">Site Loading Error:</strong>
          <span className="block sm:inline"> {siteError}</span>
        </div>
      )}
      
      {/* General Success Message */}
      {successMessage && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4" role="alert">
          <span className="block sm:inline">{successMessage}</span>
        </div>
      )}
      
      {/* Settings Loading/Saving Error */}
       {settingsError && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4" role="alert">
          <strong className="font-bold">Settings Error:</strong>
          <span className="block sm:inline"> {settingsError}</span>
        </div>
      )}

      {/* Informational Message */} 
      {infoMessage && (
        <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded mb-4" role="alert">
          <span className="block sm:inline">{infoMessage}</span>
        </div>
      )}

      {/* Site Selection */}
      <div className="mb-6">
        <label htmlFor="site-select" className="block text-sm font-medium text-gray-700 mb-1">
          Select Site:
        </label>
        <Select
          id="site-select"
          options={sites}
          value={selectedSite}
          onChange={setSelectedSite}
          isLoading={loadingSites}
          isDisabled={loadingSites || saving}
          placeholder={loadingSites ? "Loading sites..." : (sites.length > 0 ? "Select a site..." : "No sites available or failed to load")}
          classNamePrefix="react-select"
          styles={{ // Basic styling to indicate disabled state
              control: (base, state) => ({
                  ...base,
                  backgroundColor: state.isDisabled ? '#f3f4f6' : base.backgroundColor,
              }),
          }}
        />
         {loadingSites && <p className="text-xs text-gray-500 mt-1">Loading sites...</p>}
         {!loadingSites && sites.length === 0 && !siteError && (
             <p className="text-xs text-yellow-700 mt-1">No sites found.</p>
         )}
      </div>

      {/* Only show tabs and content if a site is selected */} 
      {selectedSite && (
          <div className="bg-white shadow-md rounded p-6">
              {/* Tabs Navigation */} 
              <div className="border-b border-gray-200 mb-6">
                  <ul className="flex flex-wrap -mb-px text-sm font-medium text-center">
                      <li className="mr-2">
                          <button
                              className={`inline-block p-4 rounded-t-lg ${ activeTab === 'settings' ? 'border-b-2 border-blue-600 text-blue-600' : 'border-b-2 border-transparent hover:text-gray-600 hover:border-gray-300'}`}
                              onClick={() => setActiveTab('settings')}
                              disabled={loadingSettings || saving} // Disable while loading/saving settings
                          >
                              API Settings
                          </button>
                      </li>
                      <li className="mr-2">
                          <button
                              className={`inline-block p-4 rounded-t-lg ${ activeTab === 'db_webhooks' ? 'border-b-2 border-blue-600 text-blue-600' : 'border-b-2 border-transparent hover:text-gray-600 hover:border-gray-300'}`}
                              onClick={() => setActiveTab('db_webhooks')}
                               disabled={loadingSettings || saving || loadingDbWebhooks}
                          >
                              Database Webhooks
                          </button>
                      </li>
                      <li className="mr-2">
                          <button
                              className={`inline-block p-4 rounded-t-lg ${ activeTab === 'yoco_webhooks' ? 'border-b-2 border-blue-600 text-blue-600' : 'border-b-2 border-transparent hover:text-gray-600 hover:border-gray-300'}`}
                              onClick={() => setActiveTab('yoco_webhooks')}
                              disabled={loadingSettings || saving || !settings} // Disable if no settings loaded
                          >
                              Yoco Registered Webhooks
                          </button>
                      </li>
                      <li className="mr-2">
                          <button
                              className={`inline-block p-4 rounded-t-lg ${ activeTab === 'register' ? 'border-b-2 border-blue-600 text-blue-600' : 'border-b-2 border-transparent hover:text-gray-600 hover:border-gray-300'}`}
                              onClick={() => setActiveTab('register')}
                              disabled={!settings || loadingSettings || saving} // Disable if no settings loaded
                          >
                              Register New Webhook
                          </button>
                      </li>
                  </ul>
              </div>

              {/* Tab Content */} 
              <div>
                  {/* Settings Tab Content */} 
                  {activeTab === 'settings' && (
                      <> 
                          {loadingSettings && (
                              <div className="text-center py-4">Loading settings...</div>
                          )}
                           {/* Render form if settings are loaded OR if we are creating new */} 
                           {settings && !loadingSettings && (
                               <form onSubmit={handleSettingsSubmit} > {/* Use renamed handler */}
                                  <h2 className="text-xl font-semibold mb-4">
                                      {isCreatingNew ? `Create / Overwrite Settings for ${selectedSite.label}` : `Edit Settings for ${selectedSite.label}`}
                                  </h2>
                                  {/* Form Fields (API Keys, Mode, URLs, Webhook, Active) */} 
                                  <div className="mb-4">
                                     <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="apiKeyLive">API Key (Live)</label>
                                     <input id="apiKeyLive" type="text" name="apiKeyLive" value={settings.apiKeyLive || ''} onChange={handleInputChange} disabled={saving} className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700" placeholder="sk_live_..."/>
                                  </div>
                                  <div className="mb-4">
                                     <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="apiKeyTest">API Key (Test)</label>
                                     <input id="apiKeyTest" type="text" name="apiKeyTest" value={settings.apiKeyTest || ''} onChange={handleInputChange} disabled={saving} className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700" placeholder="sk_test_..."/>
                                  </div>
                                   <div className="mb-4">
                                       <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="mode">Mode</label>
                                       <Select id="mode" name="mode" options={[{ value: 'LIVE', label: 'Live' }, { value: 'TEST', label: 'Test' }]} value={settings.mode ? { value: settings.mode, label: settings.mode === 'LIVE' ? 'Live' : 'Test' } : null} onChange={(option) => handleSelectChange('mode', option)} isDisabled={saving} classNamePrefix="react-select"/>
                                  </div>
                                  <div className="mb-4">
                                     <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="successRedirectUrl">Success Redirect URL</label>
                                     <input id="successRedirectUrl" type="url" name="successRedirectUrl" value={settings.successRedirectUrl || ''} onChange={handleInputChange} disabled={saving} className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700" placeholder="https://yourdomain.com/payment/success"/>
                                  </div>
                                  <div className="mb-4">
                                     <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="cancelRedirectUrl">Cancel Redirect URL</label>
                                     <input id="cancelRedirectUrl" type="url" name="cancelRedirectUrl" value={settings.cancelRedirectUrl || ''} onChange={handleInputChange} disabled={saving} className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700" placeholder="https://yourdomain.com/payment/cancel"/>
                                  </div>
                                   <div className="mb-4">
                                       <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="failureRedirectUrl">Failure Redirect URL</label>
                                       <input id="failureRedirectUrl" type="url" name="failureRedirectUrl" value={settings.failureRedirectUrl || ''} onChange={handleInputChange} disabled={saving} className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700" placeholder="https://yourdomain.com/payment/failure"/>
                                  </div>
                                   <div className="mb-4">
                                       <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="webhookEndpoint">Webhook Endpoint</label>
                                       <input 
                                         id="webhookEndpoint" 
                                         type="url" 
                                         name="webhookEndpoint" 
                                         value={settings.webhookEndpoint || ''} // Ensure value is controlled
                                         onChange={handleInputChange} 
                                         disabled={saving} 
                                         className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700" 
                                         placeholder="e.g., https://api.rss.solar/.../{siteUuid}" // Updated placeholder
                                       />
                                       <p className="text-xs text-gray-600 mt-1">This endpoint receives payment notifications from Yoco.</p>
                                  </div>
                                   <div className="mb-6">
                                      <label className="flex items-center">
                                          <input type="checkbox" name="isActive" checked={settings.isActive || false} onChange={handleInputChange} className="form-checkbox h-5 w-5 text-blue-600" disabled={saving}/>
                                          <span className="ml-2 text-sm text-gray-700">Settings Active</span>
                                      </label>
                                  </div>

                                  {/* Submit Button */} 
                                  <div className="flex items-center justify-between">
                                    <button
                                      className={`bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline ${(!hasChanged || saving) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                      type="submit"
                                      disabled={!hasChanged || saving}
                                    >
                                      {saving ? 'Saving...' : (isCreatingNew ? 'Create / Overwrite Settings' : 'Update Settings')}
                                    </button>
                                  </div>
                                </form>
                           )}
                           {/* Display message if settings failed to load critically */} 
                           {!settings && !loadingSettings && settingsError && (
                                <p className="text-red-600">Could not load or initialize settings form due to an error.</p>
                           )}
                      </>
                  )}

                  {/* Database Webhooks Tab Content */} 
                  {activeTab === 'db_webhooks' && (
                      <div>
                          <h2 className="text-xl font-semibold mb-4 text-gray-700">Webhooks Registered in Database</h2>
                          <WebhookDisplayList 
                              title="DB Webhooks" 
                              webhooks={dbWebhooks} 
                              isLoading={loadingDbWebhooks} 
                              errorMsg={dbWebhookError} 
                              source="DB" 
                              onViewDetails={handleViewDetails} // Pass handler
                          />
                           <p className="text-xs text-gray-500 mt-4">
                               This list shows webhooks recorded in our application database for this site.
                           </p>
                      </div>
                  )}
                  
                  {/* Yoco Registered Webhooks Tab Content */} 
                  {activeTab === 'yoco_webhooks' && (
                      <div>
                          <h2 className="text-xl font-semibold mb-4 text-gray-700">Webhooks Registered Directly with Yoco</h2>
                           <div className="mb-4">
                               <button 
                                   onClick={handleFetchYocoWebhooks} 
                                   className={`bg-purple-500 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline ${loadingYocoWebhooks ? 'opacity-50 cursor-not-allowed' : ''}`} // Correct state variable
                                   disabled={loadingYocoWebhooks} // Correct state variable
                               >
                                   {loadingYocoWebhooks ? 'Fetching from Yoco...' : 'Fetch Current Status from Yoco'} // Correct state variable
                               </button>
                           </div>
                           {yocoWebhookError && ( // Correct state variable
                               <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-3 mb-4" role="alert">
                                   <p className="font-bold">Yoco API Error</p>
                                   <p>{yocoWebhookError}</p> 
                               </div>
                           )}
                          <div className="space-y-4 mt-4">
                              <WebhookDisplayList 
                                  title="Live" 
                                  webhooks={liveWebhooks} 
                                  isLoading={loadingYocoWebhooks} // Correct state variable
                                  source="Yoco" 
                                  onViewDetails={handleViewDetails} // Pass handler
                              />
                              <WebhookDisplayList 
                                  title="Test" 
                                  webhooks={testWebhooks} 
                                  isLoading={loadingYocoWebhooks} // Correct state variable
                                  source="Yoco" 
                                  onViewDetails={handleViewDetails} // Pass handler
                              />
                          </div>
                           <p className="text-xs text-gray-500 mt-4">
                               This list is fetched directly from Yoco using the API keys saved in the 'API Settings' tab.
                           </p>
                      </div>
                  )}
                  
                  {/* Register Webhook Tab Content */} 
                  {activeTab === 'register' && (
                      <div>
                          <h2 className="text-xl font-semibold mb-4 text-gray-700">Register New Yoco Webhook</h2>
                          
                          {registrationError && (
                               <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-3 mb-4" role="alert">
                                   <p className="font-bold">Registration Error</p>
                                   <p>{registrationError}</p>
                               </div>
                           )}
                           {registrationSuccess && (
                               <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-3 mb-4" role="alert">
                                   <p>{registrationSuccess}</p>
                               </div>
                           )}
                          
                          {!settings?.webhookEndpoint && (
                              <p className="text-orange-600 mb-4">Please save a valid 'Webhook Endpoint' URL in the 'API Settings' tab before registering.</p>
                          )}
                          
                          <form onSubmit={handleWebhookRegisterSubmit}>
                              {/* Name Input */} 
                              <div className="mb-4">
                                 <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="webhookName">Webhook Name</label>
                                 <input id="webhookName" type="text" value={webhookName} onChange={(e) => setWebhookName(e.target.value)} required disabled={registeringWebhook || !settings?.webhookEndpoint} className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700" placeholder="e.g., Production Notifications"/>
                              </div>
                              
                              {/* URL Display (from settings) */} 
                               <div className="mb-4">
                                 <label className="block text-gray-700 text-sm font-bold mb-2">Webhook URL (from settings)</label>
                                 <input type="text" value={settings?.webhookEndpoint || ''} readOnly disabled className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 bg-gray-100 cursor-not-allowed"/>
                              </div>
                              
                              {/* Mode Selection */} 
                              <div className="mb-6">
                                 <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="webhookMode">Mode</label>
                                  <Select id="webhookMode" name="webhookMode" options={[{ value: 'LIVE', label: 'Live' }, { value: 'TEST', label: 'Test' }]} value={{ value: webhookMode, label: webhookMode === 'LIVE' ? 'Live' : 'Test' }} onChange={(option) => setWebhookMode(option.value)} isDisabled={registeringWebhook || !settings?.webhookEndpoint} classNamePrefix="react-select"/>
                              </div>
                              
                               {/* Submit Button */} 
                              <div className="flex items-center">
                                <button
                                  className={`bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline ${ (registeringWebhook || !settings?.webhookEndpoint) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                  type="submit"
                                  disabled={registeringWebhook || !settings?.webhookEndpoint}
                                >
                                  {registeringWebhook ? 'Registering...' : 'Register Webhook'}
                                </button>
                              </div>
                          </form>
                           <p className="text-xs text-gray-500 mt-4">
                               This will attempt to register the webhook URL saved in settings with Yoco using the API key corresponding to the selected mode.
                           </p>
                      </div>
                  )}
              </div>
          </div>
      )}

      {/* Prompt to select a site if none is selected */} 
      {!selectedSite && !loadingSites && (
         <p className="text-gray-600">Please select a site to view or create its Yoco settings.</p>
      )}

      {/* Render the Modal - this part should be correct */}
      <WebhookDetailsModal 
          isOpen={detailsModalOpen}
          onClose={handleCloseDetails}
          webhook={selectedWebhookDetails}
      />
    </div>
  );
}

export default YocoSettingsPage; 